import {
  ApolloClient, ApolloLink, createHttpLink, InMemoryCache
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { APOLLO_SERVICE_NAMES } from "@command-center-app/constants/api.const";
import { apolloErrorVar } from "./errorState";

// Load environment variables
const BASE_URL = import.meta.env.VITE_ALOHA_ADMIN;

// Optional per-service GraphQL endpoint overrides. When set (e.g. for
// local backend testing) they fully replace the default
// `${BASE_URL}/${service}/graphql` URI for that service. Leaving them
// unset keeps the production/staging behavior unchanged.
const SERVICE_ENDPOINT_OVERRIDES: Record<string, string | undefined> = {
  [APOLLO_SERVICE_NAMES.VMS]: import.meta.env.VITE_VMS_GRAPHQL_URL,
  [APOLLO_SERVICE_NAMES.CATALOG]: import.meta.env.VITE_CATALOG_GRAPHQL_URL,
};

// Create a basic HTTP link (without fixed service)
const httpLink = createHttpLink({
  uri: BASE_URL, // Default URI, but we modify it dynamically
});

// Middleware to attach headers
const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem("access-token");

  return {
    headers: {
      ...headers,
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  };
});

// Custom link to dynamically modify the URI based on the service
const dynamicServiceLink = new ApolloLink((operation, forward) => {
  const service = operation.getContext().service;
  if (!service) {
    throw new Error("Apollo Client requires a service name.");
  }

  // Dynamically modify request URI. Prefer a per-service override when
  // one is configured (local testing); otherwise fall back to the
  // default gateway-style URI.
  const uri =
    SERVICE_ENDPOINT_OVERRIDES[service] || `${BASE_URL}/${service}/graphql`;
  operation.setContext({
    uri,
  });

  return forward(operation);
});

// Apollo error handling link
const errorLink = onError(({ graphQLErrors, networkError }) => {
  let errorMessage = "";

  if (graphQLErrors) {
    errorMessage = graphQLErrors.map(({ message }) => message).join(", ");
  }

  if (networkError) {
    errorMessage = "Network error occurred. Please check your connection.";
  }

  if (errorMessage) {
    apolloErrorVar(new Error(errorMessage)); // Store error globally
  }
});

// Combine links (Order matters!)
const client = new ApolloClient({
  link: ApolloLink.from([authLink, dynamicServiceLink, errorLink, httpLink]),
  cache: new InMemoryCache(),
});

export default client;
